import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../styles/ViewTickets.css';

interface Ticket {
  ticketNumber: number;
  url: string;
  publicId: string;
  downloadUrl: string;
  isAdditional?: boolean;
}

interface BookingInfo {
  bookingCode: string;
  customerName: string;
  phone: string;
  email: string;
  eventDate: string;
  eventTime: string;
  location: string;
  boothType: string;
  boothDisplayName?: string; // الاسم المعروض من لوحة التحكم
  bookingType: string;
  totalTickets: number;
  freeTickets: number;
  additionalTickets: number;
  totalAmount: number;
}

function ViewTickets() {
  const { bookingCode, bookingId } = useParams<{ bookingCode?: string; bookingId?: string }>();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingInfo, setBookingInfo] = useState<BookingInfo | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  useEffect(() => {
    fetchTickets();
  }, [bookingCode, bookingId]);

  const fetchTickets = async (retryCount = 0) => {
    try {
      // استخدم bookingId أو bookingCode حسب المتوفر
      const identifier = bookingId || bookingCode;
      if (!identifier) {
        setError('معرف الحجز مطلوب');
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ticket-generation/public/${identifier}`);
      
      const data = await response.json();
      
      if (response.status === 202 && data.processing) {
        // التذاكر قيد المعالجة
        setIsProcessing(true);
        setError('جاري إنشاء التذاكر، يرجى الانتظار...');
        
        // إعادة المحاولة كل 5 ثوانِ لمدة أقصاها دقيقتين
        if (retryCount < 24) { // 24 * 5 ثوانِ = 2 دقيقة
          setTimeout(() => {
            fetchTickets(retryCount + 1);
          }, 5000);
        } else {
          setError('انتهت مهلة الانتظار. يرجى تحديث الصفحة والمحاولة مرة أخرى.');
          setIsProcessing(false);
        }
        return;
      }
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'لم يتم العثور على التذاكر');
      }
      
      if (data.tickets && data.bookingInfo) {
        console.log('Tickets data:', data.tickets);
        console.log('Booking info:', data.bookingInfo);
        setTickets(data.tickets);
        setBookingInfo(data.bookingInfo);
        setError(null);
        setIsProcessing(false);
      } else {
        throw new Error('لا توجد تذاكر لهذا الحجز');
      }
    } catch (err: any) {
      setError(err.message || 'حدث خطأ في جلب التذاكر');
      setIsProcessing(false);
    } finally {
      setLoading(false);
    }
  };

  const downloadAll = async () => {
    try {
      // عرض رسالة تحميل
      const downloadingMessage = document.createElement('div');
      downloadingMessage.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgba(0, 0, 0, 0.8);
        color: white;
        padding: 20px 40px;
        border-radius: 10px;
        z-index: 9999;
        font-size: 16px;
      `;
      downloadingMessage.textContent = 'جاري تجهيز ملف ZIP...';
      document.body.appendChild(downloadingMessage);

      // تحميل ملف ZIP من الخادم
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ticket-zip/download-all/${bookingCode}`);
      
      if (!response.ok) {
        throw new Error('فشل تحميل التذاكر');
      }

      // تحويل الاستجابة إلى blob
      const blob = await response.blob();
      
      // إنشاء رابط تحميل
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tickets_${bookingCode}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // تنظيف الذاكرة
      window.URL.revokeObjectURL(url);
      
      // إزالة رسالة التحميل
      document.body.removeChild(downloadingMessage);
      
    } catch (error) {
      console.error('Error downloading ZIP:', error);
      alert('حدث خطأ في تحميل التذاكر. يرجى المحاولة مرة أخرى.');
    }
  };

  if (loading) {
    return (
      <div className="view-tickets-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>جاري تحميل التذاكر...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="view-tickets-page">
        <div className="error-container">
          <h2>{isProcessing ? 'جاري المعالجة...' : 'خطأ'}</h2>
          <p>{error}</p>
          {isProcessing && (
            <div className="processing-spinner">
              <div className="spinner"></div>
              <p>يرجى عدم إغلاق هذه الصفحة</p>
            </div>
          )}
          {!isProcessing && (
            <button onClick={() => navigate('/')}>العودة للرئيسية</button>
          )}
          <button onClick={() => window.location.reload()} style={{ marginTop: '10px' }}>
            تحديث الصفحة
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="view-tickets-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="tickets-container">
        {/* Header */}
        <div className="header">
          <img src="/images/logo.png" alt="Fotomista" className="logo" />
          <h1>تذاكر الحجز رقم {bookingInfo?.bookingCode || bookingCode || bookingId}</h1>
        </div>

        {/* Download ZIP Button - at the top */}
        <div className="zip-download-section">
          <button onClick={downloadAll} className="download-zip-btn">
            <span className="download-icon">📦</span>
            تحميل جميع التذاكر ZIP ({tickets.length} تذاكر)
          </button>
        </div>

        {/* Booking Information */}
        {bookingInfo && (
          <div className="booking-info-card">
            <h2>معلومات الحجز</h2>
            <div className="info-grid">
              <div className="info-row">
                <span className="label">اسم العميل:</span>
                <span className="value">{bookingInfo.customerName}</span>
              </div>
              <div className="info-row">
                <span className="label">رقم الهاتف:</span>
                <span className="value">{bookingInfo.phone}</span>
              </div>
              <div className="info-row">
                <span className="label">البريد الإلكتروني:</span>
                <span className="value">{bookingInfo.email}</span>
              </div>
              <div className="info-row">
                <span className="label">تاريخ المناسبة:</span>
                <span className="value">
                  {new Date(bookingInfo.eventDate).toLocaleDateString('ar-SA', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div className="info-row">
                <span className="label">وقت بدء المناسبة:</span>
                <span className="value">{bookingInfo.eventTime}</span>
              </div>
              <div className="info-row">
                <span className="label">الموقع:</span>
                <span className="value">{bookingInfo.location}</span>
              </div>
              <div className="info-row">
                <span className="label">نوع البوث:</span>
                <span className="value">
                  {bookingInfo.boothDisplayName || 'نوع البوث غير محدد'}
                </span>
              </div>
              <div className="info-row">
                <span className="label">نوع الحجز:</span>
                <span className="value">{bookingInfo.bookingType === 'hourly' ? 'نظام الساعات' : 'نظام التذاكر'}</span>
              </div>
              {bookingInfo.bookingType === 'perPhoto' && (
                <>
                  <div className="info-row">
                    <span className="label">التذاكر المجانية:</span>
                    <span className="value">{bookingInfo.freeTickets || 0}</span>
                  </div>
                  {bookingInfo.additionalTickets && bookingInfo.additionalTickets > 0 && (
                    <div className="info-row">
                      <span className="label">التذاكر الإضافية:</span>
                      <span className="value">{bookingInfo.additionalTickets}</span>
                    </div>
                  )}
                  <div className="info-row">
                    <span className="label">إجمالي التذاكر:</span>
                    <span className="value highlight">{bookingInfo.totalTickets}</span>
                  </div>
                </>
              )}
              <div className="info-row">
                <span className="label">المبلغ الإجمالي:</span>
                <span className="value price">{bookingInfo.totalAmount.toLocaleString('en-US')} ريال</span>
              </div>
            </div>
          </div>
        )}

        {/* Regular Tickets Section */}
        <div className="tickets-section">
          <h2>التذاكر الأساسية ({tickets.filter(t => !t.isAdditional).length})</h2>
          <div className="tickets-grid">
            {tickets.filter(ticket => !ticket.isAdditional).map((ticket) => (
              <motion.div 
                key={ticket.ticketNumber}
                className="ticket-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: ticket.ticketNumber * 0.05 }}
              >
                <div className="ticket-preview">
                  {ticket.url.includes('.png') || ticket.url.includes('.jpg') || ticket.url.includes('.jpeg') ? (
                    <img 
                      src={ticket.url} 
                      alt={`تذكرة ${ticket.ticketNumber}`}
                      className="ticket-image"
                    />
                  ) : (
                    <div className="ticket-pdf-placeholder">
                      <div className="pdf-icon">📄</div>
                      <h4>تذكرة رقم {ticket.ticketNumber}</h4>
                      <p>ملف PDF</p>
                    </div>
                  )}
                </div>
                <div className="ticket-actions">
                  <h3>تذكرة رقم {ticket.ticketNumber}</h3>
                  <div className="button-group">
                    <a 
                      href={ticket.url} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="view-btn"
                    >
                      👁️ عرض
                    </a>
                    <a 
                      href={ticket.downloadUrl || ticket.url} 
                      download={`ticket_${bookingCode}_${ticket.ticketNumber}${ticket.url.includes('.png') ? '.png' : '.pdf'}`}
                      className="download-btn"
                    >
                      📥 تحميل
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Additional Tickets Section */}
        {tickets.filter(t => t.isAdditional).length > 0 && (
          <div className="additional-tickets-section">
            <h2>التذاكر الإضافية ({tickets.filter(t => t.isAdditional).length})</h2>
            <div className="tickets-grid">
              {tickets.filter(ticket => ticket.isAdditional).map((ticket) => (
                <motion.div 
                  key={`additional-${ticket.ticketNumber}`}
                  className="ticket-card additional-ticket"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: ticket.ticketNumber * 0.05 }}
                >
                  <div className="additional-badge">إضافية</div>
                  <div className="ticket-preview">
                    {ticket.url.includes('.png') || ticket.url.includes('.jpg') || ticket.url.includes('.jpeg') ? (
                      <img 
                        src={ticket.url} 
                        alt={`تذكرة إضافية ${ticket.ticketNumber}`}
                        className="ticket-image"
                      />
                    ) : (
                      <div className="ticket-pdf-placeholder">
                        <div className="pdf-icon">📄</div>
                        <h4>تذكرة رقم {ticket.ticketNumber}</h4>
                        <p>ملف PDF</p>
                      </div>
                    )}
                  </div>
                  <div className="ticket-actions">
                    <h3>تذكرة رقم {ticket.ticketNumber}</h3>
                    <div className="button-group">
                      <a 
                        href={ticket.url} 
                        target="_blank"
                        rel="noopener noreferrer"
                        className="view-btn"
                      >
                        👁️ عرض
                      </a>
                      <a 
                        href={ticket.downloadUrl || ticket.url} 
                        download={`additional_ticket_${bookingCode}_${ticket.ticketNumber}${ticket.url.includes('.png') ? '.png' : '.pdf'}`}
                        className="download-btn"
                      >
                        📥 تحميل
                      </a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <div className="footer">
          <p>© 2024 Fotomista - جميع الحقوق محفوظة</p>
        </div>
      </div>
    </motion.div>
  );
}

export default ViewTickets;